import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { log } from '../utils/logger';

/**
 * Overlay back-button coordinator.
 *
 * Lets the device/browser **back** gesture close the topmost open overlay
 * (modal, full-screen panel, bottom sheet) instead of navigating away — the
 * expected behaviour on mobile.
 *
 * There is exactly one browser history, and exactly one `popstate` listener in
 * the app ([useBackNavigation]). So this coordinator is a module-level singleton
 * that owns an in-memory stack of open overlays; [useBackNavigation]'s listener
 * consults it FIRST (via [handleOverlayPopstate]) and only falls through to its
 * own game-trap / location-back logic when no overlay is open.
 *
 * Each open overlay owns one extra "buffer" history entry so a back press has
 * something to consume. A back press while an overlay is open pops the buffer
 * entry → we close the overlay. A programmatic close (X / backdrop / Escape /
 * action) instead removes the buffer entry itself via `history.back()`, whose
 * resulting `popstate` is absorbed by [pendingProgrammaticPops].
 *
 * Quel `history.back()` è ASINCRONO, e questo è il motivo di
 * [runAfterProgrammaticPops]: chi vuole scrivere nella cronologia mentre degli
 * overlay si stanno chiudendo deve aspettare che i loro rewind siano arrivati,
 * o la voce nuova finisce sotto i back ancora in volo e se la mangiano.
 *
 * E QUELL'ATTESA HA UNA SCADENZA, che è la lezione di un guasto vero: il menu di
 * «Gioca» che si apriva e poi non apriva più niente, fino al ricarico. Tutte e
 * sei le sue righe passano da [runAfterProgrammaticPops] QUANDO PORTANO DA
 * QUALCHE PARTE (`dalFoglioGioca` in App.tsx) — la riga dei problemi del giorno,
 * a chi non ha un conto, non chiude il foglio e quindi non chiede nessun
 * riavvolgimento: scrive il motivo e lo lascia aperto —, e la coda si svuotava
 * SOLO quando `pendingProgrammaticPops` tornava a zero. Un `popstate` che non
 * arriva — traversate dello stesso task fuse da WebKit, una PWA congelata fra
 * il `back()` e la traversata, il listener assente perché `Sentry.ErrorBoundary`
 * ha smontato App — lasciava il contatore sopra zero PER SEMPRE: contatore di
 * modulo, e nessuno che lo rimetta a posto.
 *
 * Non si riparava nemmeno da sé, ed è la parte meno ovvia: ogni chiusura
 * successiva aggiunge uno slot E consuma un popstate, quindi il pavimento non
 * scende mai; e l'unico guaritore in-app, `tornaIndietro`, è sbarrato da
 * `stackRef.current.length > 1`, una pila che cresce solo DENTRO il lavoro
 * rimandato. Su una PWA installata, dove non c'è la freccia del browser, non
 * restava nessuna strada.
 */
interface OverlayEntry {
  token: object;
  onClose: () => void;
}

const stack: OverlayEntry[] = [];

// Counts back() calls WE issued for programmatic closes; their popstate events
// must be swallowed rather than treated as a user back press. A counter (not a
// boolean) so multiple closes in the same tick each consume exactly one event.
let pendingProgrammaticPops = 0;

/** Lavoro rimandato finché il contatore qui sopra non torna a zero. */
const drainListeners: Array<() => void> = [];

/**
 * Gli overlay REGISTRATI ma la cui voce cuscinetto non è ancora stata scritta,
 * perché un riavvolgimento era in volo quando si sono aperti (vedi `pushOverlay`).
 *
 * È un insieme e non un contatore perché la spinta rimandata va annullata se
 * quell'overlay si chiude prima che tocchi a lei: altrimenti resterebbe una voce
 * cuscinetto orfana, che è il modo in cui un «indietro» smette di uscire.
 */
const spinteRimandate = new Set<object>();

/**
 * Quanto si aspetta il `popstate` di un `back()` che abbiamo chiesto noi.
 *
 * Generoso: un popstate atterra entro un frame o due. Non si paga nel cammino
 * felice — l'evento arriva, svuota la coda e spegne il timer prima che scada —
 * e nel cammino rotto è il tempo che la navigazione arriva in ritardo invece di
 * non arrivare mai.
 */
const SCADENZA_RIAVVOLGIMENTO_MS = 300;

/** Quanto si continua a ingoiare un `popstate` arrivato dopo la scadenza. */
const GRAZIA_POP_SCADUTI_MS = 2000;

/**
 * Gli slot SCADUTI, e sono la ragione per cui non basta azzerare il contatore.
 *
 * Il loro popstate non è arrivato in tempo, ma se arriva tardi è ancora NOSTRO e
 * va ingoiato: scambiato per una pressione dell'utente chiuderebbe un overlay
 * che nessuno ha chiesto di chiudere, o farebbe una navigazione indietro
 * spuria. A differenza di `pendingProgrammaticPops`, però, NON trattiene la
 * coda — è esattamente la distinzione che mancava.
 */
let popScaduti = 0;

let scadenza: ReturnType<typeof setTimeout> | null = null;
let graziaScaduti: ReturnType<typeof setTimeout> | null = null;

function scarica(): void {
  if (drainListeners.length === 0) return;
  // Si svuota PRIMA di chiamare, così un listener che ne accodasse un altro non
  // gira a vuoto dentro l'iterazione.
  const queued = drainListeners.splice(0, drainListeners.length);
  for (const fn of queued) fn();
}

function annullaScadenza(): void {
  if (scadenza === null) return;
  clearTimeout(scadenza);
  scadenza = null;
}

function armaGraziaScaduti(): void {
  if (graziaScaduti !== null) clearTimeout(graziaScaduti);
  graziaScaduti = setTimeout(() => {
    graziaScaduti = null;
    // Una traversata che non è atterrata in due secondi non atterrerà mai.
    // Tenere lo slot vorrebbe dire ingoiare il PROSSIMO «indietro» vero.
    popScaduti = 0;
  }, GRAZIA_POP_SCADUTI_MS);
}

function armaScadenza(): void {
  if (scadenza !== null) return;
  scadenza = setTimeout(() => {
    scadenza = null;
    const mancanti = pendingProgrammaticPops;
    if (mancanti === 0) return;
    popScaduti += mancanti;
    pendingProgrammaticPops = 0;
    armaGraziaScaduti();
    // Senza queste due righe il guasto è MUTO, ed è così che è durato: in
    // produzione `log.warn` è spento (logger.ts), e il sintomo — «il menu si
    // apre e non apre niente» — non somiglia per niente alla sua causa.
    log.warn('OverlayBack', 'popstate mai arrivato: coda liberata a forza', {
      mancanti,
      rimandati: drainListeners.length,
      aperti: stack.length,
      historyLength: typeof window !== 'undefined' ? window.history.length : -1,
    });
    Sentry.captureMessage('overlay back: popstate mancante', {
      level: 'warning',
      tags: { overlayPopMancante: 'true' },
      extra: { mancanti, rimandati: drainListeners.length, aperti: stack.length },
    });
    scarica();
  }, SCADENZA_RIAVVOLGIMENTO_MS);
}

/**
 * Esegue `fn` quando ogni chiusura programmatica ha DAVVERO riavvolto la
 * cronologia — subito, se non ce n'è nessuna in volo, e al più tardi dopo
 * [SCADENZA_RIAVVOLGIMENTO_MS] se un `popstate` non arriva.
 *
 * Serve a una cosa sola, ed è il difetto che ha motivato questa funzione:
 * chiudere due overlay e cambiare schermata nello stesso commit metteva la voce
 * della schermata nuova PRIMA che i due `history.back()` arrivassero. `pushState`
 * tronca le voci in avanti, quindi quei due back scendevano di due partendo dalla
 * voce appena creata: si mangiavano lei e un buffer, e il cursore finiva sulla
 * voce base dell'app. Il risultato visibile era che il primo «indietro» dopo aver
 * avviato una partita dal foglio usciva dall'app.
 */
export function runAfterProgrammaticPops(fn: () => void): void {
  if (pendingProgrammaticPops === 0) {
    fn();
    return;
  }
  drainListeners.push(fn);
  // Di norma il timer è già armato da `consumeOverlay`, che è l'unico posto che
  // incrementa. Riarmarlo qui è la cintura: una coda non deve poter restare
  // appesa a un contatore che nessuno ha messo in scadenza.
  armaScadenza();
}

/** Scrive la voce cuscinetto, preservando `history.state` (l'`idx` di
 *  [useBackNavigation] deve restare quello che era). */
function scriviCuscinetto(): void {
  window.history.pushState(
    { ...window.history.state, overlay: true },
    '',
    window.location.href
  );
}

/**
 * Register an opening overlay. Pushes a buffer history entry (preserving the
 * current history.state so [useBackNavigation]'s `idx` bookkeeping stays intact)
 * and returns a token used later to close it.
 *
 * LA SPINTA ASPETTA, se un riavvolgimento programmatico è in volo. È lo stesso
 * patto di [runAfterProgrammaticPops] — `pushState` tronca le voci in avanti,
 * quindi scrivere mentre un `back()` non è ancora atterrato mette la voce nuova
 * dove il back passerà a prendersela — applicato alla voce cuscinetto invece che
 * a quella di una schermata.
 *
 * Senza questa attesa l'invariante del coordinatore («con N overlay aperti, il
 * cursore sta sul cuscinetto dell'ultimo») si rompe, e ogni «indietro» successivo
 * si mangia una voce VERA dell'app invece del cuscinetto. Misurato in sviluppo,
 * dove StrictMode monta ogni effetto due volte (setup → cleanup → setup): la
 * sequenza è push → back() → push, e il secondo push partiva prima che il back
 * atterrasse. Il cursore finiva sulla voce base — `history.state` senza
 * `overlay: true`, misurato — con l'overlay comunque in pila, e con due pannelli
 * sovrapposti bastava un «indietro» per uscire dall'app. In produzione React non
 * raddoppia i montaggi e questo ramo non si percorre quasi mai; quando si
 * percorre (un overlay che si apre nello stesso tick in cui un altro si chiude)
 * fa la cosa giusta per la stessa ragione.
 */
export function pushOverlay(onClose: () => void): object {
  const token = {};
  stack.push({ token, onClose });

  if (pendingProgrammaticPops === 0) {
    scriviCuscinetto();
    return token;
  }

  /*
   * Non serve una guardia gemella in `handleOverlayPopstate` — quella che toglie
   * dalla pila l'overlay in cima a un «indietro» dell'utente — ed è utile saperlo
   * prima di aggiungercene una: un «indietro» dell'utente NON può togliere di
   * mezzo un overlay la cui spinta è ancora rimandata. Finché il contatore sta
   * sopra zero ogni `popstate` viene assorbito come riavvolgimento nostro, e lo
   * zero arriva insieme a `scarica()`, che la spinta la scrive. Le due strade —
   * il popstate o la scadenza — passano tutt'e due di lì.
   */
  spinteRimandate.add(token);
  runAfterProgrammaticPops(() => {
    // Chiuso prima che toccasse a lui: la spinta si annulla, o lascia una voce
    // cuscinetto che non è di nessuno.
    if (!spinteRimandate.delete(token)) return;
    scriviCuscinetto();
  });
  return token;
}

/**
 * Programmatic close (X / backdrop / Escape / action). If the entry is still in
 * the stack — i.e. it was NOT already removed by a back press — drop it and
 * consume its buffer history entry. No-op (idempotent) once a back press has
 * already removed it.
 */
export function consumeOverlay(token: object): void {
  const i = stack.findIndex((e) => e.token === token);
  if (i === -1) return;
  stack.splice(i, 1);
  // La sua voce cuscinetto non è mai stata scritta (era rimandata): si annulla la
  // spinta e basta. Un `back()` qui scenderebbe su una voce di qualcun altro.
  if (spinteRimandate.delete(token)) return;
  // Only consume a buffer history entry when we are actually sitting on one.
  // Under React StrictMode (dev) the effect's setup→cleanup→setup cycle
  // interleaves with the asynchronous `history.back()`, and the browser can end
  // up parked on the app's base entry while this token is still live. Calling
  // `history.back()` then would traverse BELOW the app root and exit the app
  // (the "X closes the modal but leaves the app" bug). The buffer marker on
  // `history.state` tells us whether a back() is safe.
  //
  // ATTENZIONE AL LIMITE: `overlay: true` dice «questa voce è UN buffer», non
  // «è IL TUO buffer», e `window.history.state` è vecchio mentre un `back()` è
  // in volo — quindi due chiusure dello stesso tick leggono lo stesso valore.
  // È voluto (i test lo fissano: due chiusure nello stesso commit devono
  // chiedere due riavvolgimenti), ma è anche il motivo per cui la scadenza qui
  // sotto non è un lusso.
  const onBufferEntry =
    typeof window !== 'undefined' &&
    (window.history.state as { overlay?: boolean } | null)?.overlay === true;
  if (!onBufferEntry) return;
  pendingProgrammaticPops += 1;
  armaScadenza();
  window.history.back();
}

/**
 * Un `history.back()` NOSTRO chiesto da fuori: il suo `popstate` va ingoiato
 * esattamente come quello di una chiusura programmatica, altrimenti il gestore
 * delle posizioni — o la pila degli overlay — lo prenderebbe per un «indietro»
 * dell'utente.
 *
 * Lo usa l'osservatore di [useBackNavigation] per COLLASSARE due voci gemelle:
 * quando una sostituzione scriverebbe la stessa posizione che sta gia' nella
 * voce sotto, quella voce non va riscritta — va tolta, tornando a quella di
 * sotto. Senza, ogni turno di torneo lasciava una voce in piu' per la stessa
 * pagina (damiera spinta, poi sostituita dalla pagina di partenza), e uscirne
 * costava una pressione dell'indietro in cui a schermo non cambia niente.
 *
 * Torna `false` — e chi chiama si tiene il comportamento di prima — quando
 * riavvolgere NON e' sicuro. Sono due guasti veri, non prudenza generica:
 *
 *  - **un overlay aperto**: il cursore sta sul suo CUSCINETTO, non su una voce
 *    dell'app. Il `back()` porterebbe via quello invece della gemella, e
 *    l'invariante del coordinatore — «con N overlay aperti il cursore sta sul
 *    cuscinetto dell'ultimo» — si romperebbe; il `popstate`, poi, verrebbe
 *    assorbito qui sotto e non chiuderebbe nessun overlay, ma la cronologia
 *    resterebbe sfalsata di una voce.
 *  - **un riavvolgimento gia' in volo**: il nostro `popstate` finirebbe in una
 *    coda che non e' la sua, e `pushState` tronca le voci in avanti. E' la
 *    famiglia di guasti per cui esiste [runAfterProgrammaticPops].
 *
 * ⚠️ Chi chiama deve aggiornare il PROPRIO specchio **prima** di chiamarla: il
 * `popstate` puo' atterrare entro un frame, e deve trovare la contabilita' in
 * pari. E se non atterrasse mai, vale la stessa rete di `consumeOverlay`: la
 * scadenza qui sopra sposta lo slot in `popScaduti`, quindi al massimo si paga
 * UNA pressione ingoiata e il gesto riprende.
 */
export function provaARiavvolgereUnaVoce(): boolean {
  if (typeof window === 'undefined') return false;
  if (stack.length > 0 || pendingProgrammaticPops > 0 || popScaduti > 0) return false;
  pendingProgrammaticPops += 1;
  armaScadenza();
  window.history.back();
  return true;
}

/**
 * Consulted FIRST by [useBackNavigation]'s popstate handler. Returns true when
 * the event was an overlay event (and was handled here), false to fall through
 * to the existing game-trap / location-back logic.
 */
export function handleOverlayPopstate(): boolean {
  if (pendingProgrammaticPops > 0) {
    // The popstate from a `history.back()` we issued for a programmatic close.
    pendingProgrammaticPops -= 1;
    // Ultimo arrivato: la cronologia è di nuovo dove deve essere, e chi aspettava
    // di scriverci può farlo.
    if (pendingProgrammaticPops === 0) {
      annullaScadenza();
      scarica();
    }
    return true;
  }
  // Arrivato DOPO la scadenza: la coda è già stata liberata, ma l'evento è
  // ancora nostro e va ingoiato comunque — non è un «indietro» dell'utente.
  if (popScaduti > 0) {
    popScaduti -= 1;
    return true;
  }
  const top = stack.pop();
  if (!top) return false;
  top.onClose();
  return true;
}

/** Test-only: reset the singleton between cases. */
export function __resetOverlayBackButton(): void {
  stack.length = 0;
  spinteRimandate.clear();
  pendingProgrammaticPops = 0;
  popScaduti = 0;
  drainListeners.length = 0;
  annullaScadenza();
  if (graziaScaduti !== null) {
    clearTimeout(graziaScaduti);
    graziaScaduti = null;
  }
}

/*
 * La sonda, solo in sviluppo: `window.__overlayPops()` dalla console dice se il
 * contatore è incastrato mentre un menu è morto. È la differenza fra misurare e
 * indovinare — vedi la testata.
 */
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { __overlayPops: () => unknown }).__overlayPops = () => ({
    pendingProgrammaticPops,
    popScaduti,
    rimandati: drainListeners.length,
    aperti: stack.length,
    historyLength: window.history.length,
    state: window.history.state,
  });
}

/**
 * Close `isOpen` overlay on a back press. Call once per overlay; pass `true` for
 * overlays that are mounted only while open, or the visibility prop for modals
 * that render `null` when closed (call it BEFORE the early return).
 */
export function useOverlayBackButton(isOpen: boolean, onClose: () => void): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;
    const token = pushOverlay(() => onCloseRef.current());
    return () => consumeOverlay(token);
  }, [isOpen]);
}
